import { useRef, useCallback, useEffect } from 'react';

interface UseTouchNavigationProps {
  onSwipeLeft: () => void;
  onSwipeRight: () => void;
  threshold?: number;
  enabled?: boolean;
}

interface TouchPosition {
  x: number;
  y: number;
  time: number;
}

export function useTouchNavigation({
  onSwipeLeft,
  onSwipeRight,
  threshold = 50,
  enabled = true
}: UseTouchNavigationProps) {
  const touchStartRef = useRef<TouchPosition | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isSwipingRef = useRef(false);

  const handleTouchStart = useCallback((e: TouchEvent) => {
    if (!enabled) return;
    
    const touch = e.touches[0];
    touchStartRef.current = {
      x: touch.clientX,
      y: touch.clientY,
      time: Date.now()
    };
    isSwipingRef.current = false;
  }, [enabled]);

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (!enabled || !touchStartRef.current) return;

    const touch = e.touches[0];
    const deltaX = touch.clientX - touchStartRef.current.x;
    const deltaY = touch.clientY - touchStartRef.current.y;
    
    // Check if this is a horizontal swipe (more horizontal than vertical movement)
    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 20) {
      e.preventDefault(); // Prevent scrolling during horizontal swipe
      isSwipingRef.current = true;
    }
  }, [enabled]);

  const handleTouchEnd = useCallback((e: TouchEvent) => {
    if (!enabled || !touchStartRef.current) return;

    const touch = e.changedTouches[0];
    const deltaX = touch.clientX - touchStartRef.current.x;
    const deltaY = touch.clientY - touchStartRef.current.y;
    const deltaTime = Date.now() - touchStartRef.current.time;
    
    // Only consider it a swipe if:
    // 1. It's primarily horizontal movement
    // 2. The distance exceeds the threshold
    // 3. It completed within a reasonable time (not too slow)
    const isHorizontalSwipe = Math.abs(deltaX) > Math.abs(deltaY);
    const exceedsThreshold = Math.abs(deltaX) > threshold;
    const isQuickEnough = deltaTime < 500;
    
    if (isHorizontalSwipe && exceedsThreshold && isQuickEnough) {
      if (deltaX > 0) {
        onSwipeRight(); // Swipe right - go to previous
      } else {
        onSwipeLeft(); // Swipe left - go to next
      }
    }
    
    touchStartRef.current = null;
    isSwipingRef.current = false;
  }, [enabled, threshold, onSwipeLeft, onSwipeRight]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !enabled) return;

    // Add touch event listeners
    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, [enabled, handleTouchStart, handleTouchMove, handleTouchEnd]);

  return {
    containerRef,
    isSwipingRef
  };
}